import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { alpha } from '@mui/material';
import { QcsPaper } from '../common/basic/QcsPaper';
import { EmptyState } from './components/AnalyticsSharedStyles';

const fadeInUp = keyframes({
  from: { opacity: 0, transform: 'translateY(16px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

export const AnalyticsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  flex: 1,
  minHeight: 0,
});

export const PageHeader = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
  },
}));

export const StepBody = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  minWidth: 0,
});

export const TypeCardsRow = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1.25rem',
});

export const CollapseSection = styled.div<{ open: boolean }>(({ open }) => ({
  display: 'grid',
  gridTemplateRows: open ? '1fr' : '0fr',
  transition: 'grid-template-rows 500ms cubic-bezier(0.4, 0, 0.2, 1)',
  pointerEvents: open ? 'auto' : 'none',
}));

export const CollapseSectionInner = styled.div({
  overflow: 'hidden',
  minHeight: 0,
});

export const OverviewPane = styled.div<{ $active: boolean }>(({ $active }) => ({
  display: $active ? 'block' : 'none',
}));

export const ExplorerPane = styled.div<{ $active: boolean }>(({ $active }) => ({
  display: $active ? 'flex' : 'none',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
}));

export const FadeIn = styled.div({
  animation: `${fadeInUp} 450ms cubic-bezier(0.4, 0, 0.2, 1) both`,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const BuilderActions = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
});

export const KpiStripWrapper = styled.div(({ theme }) => ({
  position: 'relative',

  '& > .kpi-row-host': {
    borderRadius: '12px',
    outline: '1px dashed transparent',
    outlineOffset: '2px',
    transition: 'outline-color 240ms ease, outline-offset 240ms ease',
  },

  '& > .kpi-row-host > *': {
    transition: 'opacity 260ms ease, transform 260ms ease, filter 260ms ease',
  },

  '&[data-previewing="true"]': {
    '& > .kpi-row-host': {
      outlineColor: alpha(theme.palette.primary.main, 0.5),
      outlineOffset: '6px',
    },

    '& > .kpi-row-host > *': {
      opacity: 0.55,
      transform: 'scale(0.99)',
      filter: 'saturate(0.85)',
    },
  },
}));

export const KpiDismissChip = styled.button(({ theme }) => ({
  position: 'absolute',
  top: '-14px',
  right: '8px',
  zIndex: 3,

  display: 'inline-flex',
  alignItems: 'center',

  padding: '4px 7px',
  margin: 0,

  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '999px',
  color: theme.palette.text.secondary,
  cursor: 'pointer',

  fontFamily: 'inherit',
  fontSize: '0.68rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  lineHeight: 1,

  boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,

  transition:
    'background-color 220ms ease, border-color 220ms ease, color 220ms ease, box-shadow 220ms ease, padding 220ms ease, transform 100ms ease',

  '& .kpi-dismiss-chip__icon': {
    fontSize: '0.95rem',
    flexShrink: 0,
  },

  '& .kpi-dismiss-chip__label': {
    maxWidth: 0,
    marginRight: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    opacity: 0,
    transition: 'max-width 280ms ease, margin-right 280ms ease, opacity 220ms ease',
  },

  '&:hover, &:focus-visible': {
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.12)})`,
    borderColor: alpha(theme.palette.primary.main, 0.55),
    color: theme.palette.primary.main,
    boxShadow: `0 3px 12px ${alpha(theme.palette.primary.main, 0.22)}`,
    paddingLeft: '11px',

    '& .kpi-dismiss-chip__label': {
      maxWidth: '220px',
      marginRight: '0.4rem',
      opacity: 1,
    },
  },

  '&:focus-visible': {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    outlineOffset: 2,
  },

  '&:active': {
    transform: 'scale(0.96)',
  },
}));

export const KpiRow = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1.5fr 1fr',
  gap: '1rem',
  '& > *': {
    animation: `${fadeInUp} 400ms cubic-bezier(0.4, 0, 0.2, 1) both`,
  },
  '& > *:nth-child(1)': { animationDelay: '0ms' },
  '& > *:nth-child(2)': { animationDelay: '80ms' },
  '& > *:nth-child(3)': { animationDelay: '160ms' },
  '& > *:nth-child(4)': { animationDelay: '240ms' },
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@media (max-width: 500px)': {
    gridTemplateColumns: '1fr',
  },
});

export const DrillDownBadge = styled.div(({ theme }) => ({
  marginTop: '0.5rem',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.625rem',
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  '& > button': {
    appearance: 'none',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
    background: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    padding: '4px 12px 4px 8px',
    fontSize: 'inherit',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    borderRadius: '999px',
    transition: 'background-color 150ms ease, border-color 150ms ease',
    '& > svg': { fontSize: '1.05rem' },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
      borderColor: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  '& > .drill-separator': {
    color: theme.palette.text.disabled,
  },
}));

/* ---------- Dashboard-specific ---------- */

export const DashboardGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gridAutoRows: '171px',
  gridAutoFlow: 'dense',
  gap: '1.25rem',
});

export const MiniChartTile = styled(QcsPaper)<{ compact?: boolean }>(({ compact }) => ({
  padding: compact ? '1rem' : '1.25rem',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'box-shadow 200ms ease, transform 200ms ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.10)',
    transform: 'translateY(-2px)',
  },
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  height: compact ? '171px' : '362px',
  boxSizing: 'border-box',
}));

export const FilterToggleBar = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  flexWrap: 'wrap',
});

export const FilterChipsRow = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
});

/* ---------- Overview empty state ---------- */

export const OverviewEmptyState = styled(EmptyState)({
  padding: '4rem 2rem',
});

export const OverviewEmptyTitle = styled.span({
  fontSize: '1rem',
  fontWeight: 500,
});

export const OverviewEmptyHint = styled.span({
  fontSize: '0.85rem',
});

/* ---------- Overview-tab badge ---------- */

export const MiniChartBadge = styled.span(({ theme }) => ({
  display: 'inline-block',
  borderRadius: '999px',
  padding: '0.2rem 0.6rem',
  background: `${theme.palette.primary.main}14`,
  color: theme.palette.primary.main,
  fontSize: '0.7rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  lineHeight: 1.4,
}));

/* ---------- Explorer two-column layout ---------- */

export const ExplorerLayout = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '320px 1fr',
  gap: '1.5rem',
  flex: 1,
  minHeight: 0,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ExplorerFilterPanel = styled.div(({ theme }) => ({
  alignSelf: 'start',
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  padding: 0,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  // Compact all MUI inputs inside the panel
  '& .MuiInputBase-root': {
    fontSize: '0.85rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem',
  },
  '& .MuiAutocomplete-inputRoot': {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  [theme.breakpoints.down('md')]: {
    position: 'static',
    maxHeight: 'none',
  },
}));

export const FilterPanelScroll = styled.div({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
});

export const FilterPanelFooter = styled.div(({ theme }) => ({
  position: 'relative',
  padding: '0.75rem 1rem',
  display: 'flex',
  justifyContent: 'flex-end',
  background: theme.palette.background.paper,
  borderRadius: '0 0 12px 12px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-16px',
    left: 0,
    right: 0,
    height: '16px',
    background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
    pointerEvents: 'none',
  },
}));
